

  
const content = `# SecureFile Settings

The settings for the SecureFile product are managed at the OnData Portal. As a System Administrator or Security Administrator, you can navigate to the \`Settings\` -> 
\`SecureFile\` page to manage the SecureFile settings.  
  
## 1. Microsoft Encrypted File System
  
This setting is only applicable to the SecureFile clients running on Windows operation systems. If the Microsoft Encrypted File System option is turned on for all Windows 
computers in your domain, you can optionally enable this setting to protect your sensitive files with additional security built-in with Windows. When a sensitive file 
protected with OnData SecureFile (an OSF file) is temporarily made available for your authorized users to access, that temporary file is protected by Windows if the 
Microsoft Encrypted File System option is turned on. Any other users, including unauthorized users and even administrative users, cannot access that temporary file. Only 
the authorized user can access that temporary file, which is automatically deleted when the authorized user closes the file or associated application.  
  
## 2. Decryption Legal Warning
  
When an authorized user tries to decrypt a sensitive file protected by OnData SecureFile (an OSF file), you can optionally display a legal warning. The user needs to 
acknowledge the legal warning before proceeding to decrypt the file. You can configure the verbiage for the legal warning.  
  
## 3. Offline Access
  
This setting determines whether your users can access sensitive files protected with OnData SecureFile (OSF files) when they are offline (i.e. no access to the Internet). 
If enabled, your users can temporarily enable offline access to certain sensitive files they are authorized to access using the SecureFile App. By doing so, they can 
specify a password to access such files in offline mode, the maximum number of times offline access is allowed for a file, and an expiration date for offline access. 
  
As a System Administrator or Security Administrator, you can further configure the following settings if offline access is enabled:  
a. \`Auto Remove Offline Access\`: If this option is selected, the offline access to all files will be turned off when the SecureFile App gets online. No action is required 
for your users. If this option is not selected, the SecureFile App will prompt your users whether to turn off offline access or not when getting online  
b. \`Maximum Offline Operations per file\`: When your users configure the maximum number of times offline access is allowed for a file, they cannot choose a number larger 
than this number  
c. \`Maximum Offline Validity Period in days\`: When your users configure an expiration date for offline access, they cannot choose a date that exceeds the number of days 
defined here  
  
## 4. Auto Discovery

This setting determines whether your SecureFile clients will automatically scan, classify, and protect your sensitive files. If enabled, the SecureFile clients will 
periodically scan the designated folders and sub-folders recursively. Further, they will automatically apply the file classification rules and file protection rules. 
The sensitive files meeting the criteria specified in those rules will be automatically classified and/or encrypted. Please refer to 
[**File Classification and Protection Rules**](reference_doc#how-to_guides/securefile/classification_and_protection_rules) on how to configure file classification rules and file protection rules.  
  
If Auto Discovery is enabled, you can further configure the following settings:  
a. \`Scan Interval\`: This setting specifies how often the SecureFile client should scan the designated folders and sub-folders recursively for new or updated files  
b. \`Scan Root Folders\`: This setting designates one or more root folders that should be scanned periodically. The SecureFile clients will scan all root folders 
and associated sub-folders recursively. A root folder should be an absolute path, not a relative path. After you enter a root folder, you can press the \`Enter\` key 
to confirm, and enter additional root folders as needed. 
  * You can use system-level environmental variables (not user-level environmental variables) in a root folder, such as %UserProfile%&bsol;Documents. In that case, the tool 
  will scan the \`Documents\` folder under each user's home folder, such as \`C:&bsol;Users&bsol;<username>&bsol;Documents\`. The system-level environmental variable should point to a 
  single directory on the target computer, not multiple directories.
  * Some of the well-known system folders will be automatically excluded and cannot be added as a root folder, including: 
    * \`{DriveLetter}:&bsol;Program Files\` and sub folders
    * \`{DriveLetter}:&bsol;Program Files (x86)\` and sub folders
    * \`C:&bsol;Windows\` and sub folders​
    * Auto-generated hidden folders and sub folders such as \`C:&bsol;ProgramData\`, \`C:&bsol;Recovery\`, \`C:&bsol;OneDriveTemp\`
    * System root folder \`C:&bsol;\`
    * Users root folder \`C:&bsol;Users\`
    * User home folders \`C:&bsol;Users&bsol;<username>\`, but you can designate specific sub folders under \`C:&bsol;Users&bsol;<username>\`
    * AppData folder under user home folders \`C:&bsol;Users&bsol;<username>&bsol;AppData\` and sub folders
    * OnData folder under user home folders \`C:&bsol;Users&bsol;<username>&bsol;OnData\` and sub folders 
    * Folders starting with $ under disk root folers, such as \`C:&bsol;$WinREAgent\` and \`D:&bsol;$RECYCLE.BIN\`

c. \`Configure Per Client\`: By default, all SecureFile clients will perform automated discovery, classification, and protection periodically if the 
\`Enabled Auto Discovery\` global setting is turned on. But if you would like to turn off automated scan on a specific SecureFile client, you can click on the 
\`Configure Per Client\` link, locate that client, and turn off \`Auto Discovery\` for that client. Also, you can configure the list of folders to scan for each client. 
If configured, only the folders configured specifically for this client will be scanned. The default folders configured in \`Step b.\` above won't be scanned on this 
client. Similarly, the well-known system folders listed above will be automatically excluded from the client folder configuration as well.  
d. \`Enable Data Analyzer\` and \`Configure Data Analyzer\`: Please refer to the [**Deploy Data Analyzer**](reference_doc#how-to_guides/securefile/deploy_data_analyzer) page on how to deploy and configure 
the Data Analyzer component.  
  
## 5. File Types to Scan with Auto Discovery

When Auto Discovery is enabled, OnData SecureFile will automatically scan the following types of files in the designated folders to look for sensitive data.  
* Compiled HTML Help file (.chm)
* CSV files (.csv)
* Email message files (.eml, .msg)
* HTML files (.html, .htm, .mhtml)
* JSON files (.json)
* Markdown documents (.md)
* PDF documents (.pdf)
* Microsoft Access files (.accdb, .mdb)
* Microsoft Excel documents (.xls, .xlt, .ods, .xla, .xlsx, .xlsm, .xlsb, .xltx, .xltm, .xlam)
* Microsoft PowerPoint documents (.ppt, .pps, .pot, .odp, .pptx, .pptm, .ppsx, .ppsm, .potx, .potm)
* Microsoft Publisher documents (.pub, .pubx)
* Microsoft Visio documents (.vsdx, .vsdm, .vssm, .vssx, .vstm, .vstx)
* Microsoft Word documents (.doc, .dot, .rtf, .odt, .docx, .docm, .dotx, .dotm)
* Microsoft SQL scripts (.sql)
* Text documents (.txt)
* XMind Workbook files (.xmind)
* XML files (.xml)
* Image files (.png, .jpg, .jpeg, .tiff, .tif, .gif, .bmp)

  
  
  `
  
export default content;
